import { useContext } from 'react'
import { CircularProgress, makeStyles } from '@material-ui/core'
import HelpIcon from '@material-ui/icons/Help'

import Pagination from '@material-ui/lab/Pagination'

import Result from '../components/Result.tsx'
import { AppContext } from '../state/Context.tsx'
import { setQueryParam } from '../lib/queryParams.ts'
import { useHtmlId } from '../hooks/useHtmlId.ts'
import { useHistory } from 'react-router-dom'
import { setTitle } from '../lib/setTitle.ts'
import { LocaleStringifier, Truncator } from '../lib/formatters.ts'
import { RESULTS_PER_PAGE, TITLE_SEGMENT_TRUNCATE_LENGTH } from '../config.ts'
import Link from './Link.tsx'

function ResultsList({
	results,
	page,
}: { page: number; results: CedictEntry[] }) {
	return (
		<>
			{results
				.slice((page - 1) * RESULTS_PER_PAGE, page * RESULTS_PER_PAGE)
				.map((result, idx) => {
					return (
						<Result
							key={idx}
							entry={result}
							isLast={idx === results.length - 1}
						/>
					)
				})}
		</>
	)
}

const useStyles = makeStyles((theme) => ({
	loaderOuter: {
		marginTop: '2em',
		display: 'flex',
		justifyContent: 'center',
	},
	loaderInner: {
		zoom: 1.2,
	},
	resultsOuter: {
		margin: '1.5em 0',
		display: 'flex',
	},
	resultsTotalDisplay: {
		padding: '0 20px',
	},
	halfWidthHr: {
		flexGrow: 1,
		height: 0,
	},
	helpLinkContainer: {
		padding: '.6em',
	},
	helpLink: {
		display: 'flex',
		alignItems: 'center',
		color: `${theme.palette.secondary.main} !important`,
	},
	helpIcon: {
		paddingRight: '.1em',
		fontSize: '1.1em',
	},
}))

function ResultsDisplay() {
	const { state, dispatch } = useContext(AppContext)

	const {
		loaderOuter,
		loaderInner,
		resultsOuter,
		resultsTotalDisplay,
		halfWidthHr,
		helpLinkContainer,
		helpLink,
		helpIcon,
	} = useStyles()

	const history = useHistory()

	const outputId = useHtmlId('output')

	const {
		results,
		resultsLoading,
		page: _page,
		searchQuery,
		searchType,
	} = state

	const page = _page || 1

	if (results && !resultsLoading) {
		setTitle(
			[
				new Truncator(TITLE_SEGMENT_TRUNCATE_LENGTH).truncate(searchQuery),
				page > 1 ? `page ${page}` : '',
				searchType === 'advanced' ? 'Advanced Search' : 'Search',
			].filter(Boolean),
		)
	}

	return (
		<>
			{!resultsLoading && !results && searchType === 'advanced' && (
				<div className={helpLinkContainer}>
					<Link
						to='/instructions#advanced-search'
						className={helpLink}
					>
						{/* @ts-ignore TODO: types */}
						<HelpIcon className={helpIcon} />
						Help
					</Link>
				</div>
			)}
			<output id={outputId} className='output'>
				{resultsLoading
					? (
						<div className={loaderOuter}>
							<CircularProgress
								className={loaderInner}
								color='primary'
							/>
						</div>
					)
					: (
						results && (
							<>
								<div className={resultsOuter}>
									<hr className={halfWidthHr} />
									<div className={resultsTotalDisplay}>
										<strong>
											{new LocaleStringifier('en-US').stringify(
												results.length,
											)}
										</strong>{' '}
										<span>
											{results.length === 1 ? 'result' : 'results'}
										</span>
									</div>
									<hr className={halfWidthHr} />
								</div>
								{results.length > 0 && <ResultsList page={page} results={results} />}

								{/* @ts-ignore TODO: types */}
								<Pagination
									page={page}
									onChange={(_e: any, n: number) => {
										dispatch({ page: n })

										setQueryParam(
											'page',
											n.toString(),
											true,
											history,
										)

										window.scrollTo(0, 0)
									}}
									count={Math.floor(
										results.length / RESULTS_PER_PAGE,
									)}
								/>
							</>
						)
					)}
			</output>
		</>
	)
}

export default ResultsDisplay
